import React, { useState, useEffect, useCallback } from 'react';
import GlassSelect from './GlassSelect';
import ContactPathLink from './ContactPathLink';
import { SectorLensPanel } from './SectorLens';
import WideTableScroll from './WideTableScroll';
import { formatBackendDetail, messageFor } from '../lib/apiErrors';
// Iter 36f-1.5 — pure-helper module that maps the Iter 36f-1 fields
// (`tracker_priority_score`, `recommended_role`,
// `marketplace_role_explanation`) onto the Priority column +
// Role chip rendering. No backend changes; presentation-only.
import {
  getPriorityValue,
  getPriorityColorClass,
  getPriorityTooltip,
  getRoleChip,
  getRoleSubline,
} from '../lib/trackerMarketplaceDisplay';

// Iter 32n-fix — calm, contextual messages for Tracker action failures.
// `messageFor()` already maps raw `TypeError: Failed to fetch` to its
// network bucket; we override the network copy per surface so the
// banner reads naturally for the action that just failed.
const TRACKER_NET_MSG = {
  scan:    'Tracker refreshed, but one follow-up request failed. Please retry.',
  default: 'Network refresh failed. Please retry.',
};
const trackerErrMsg = (e, kind = 'default') =>
  messageFor(e, { context: 'tracker', messages: { network: TRACKER_NET_MSG[kind] || TRACKER_NET_MSG.default } });

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCY_OPTIONS = [
  { value: 'on_demand', label: 'On Demand' },
  { value: '12_hours', label: 'Every 12 hours' },
  { value: 'daily', label: 'Daily' },
  { value: '2_days', label: 'Every 2 days' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: '6_months', label: 'Every 6 months' },
];

const TREND_STYLES = {
  new: { label: 'NEW', cls: 'badge-high', color: 'var(--success)' },
  rising: { label: 'RISING', cls: 'badge-medium', color: 'var(--warning)' },
  stable: { label: 'STABLE', cls: 'badge-type', color: 'var(--text-secondary)' },
  declining: { label: 'DECLINING', cls: 'badge-low', color: 'var(--text-secondary)' },
};

function IndustryTrackerDashboard({ apiKey, onSelectCompany }) {
  const [sectors, setSectors] = useState([]);
  // Display-label overrides keyed by canonical sector value. Friendlier
  // text in the dropdown / chips without changing the persisted value
  // (e.g. "Logistics & Supply Chain" → "Logistics & Supply Chain / WMS").
  const [sectorLabels, setSectorLabels] = useState({});
  const [profiledSectors, setProfiledSectors] = useState([]);
  const [config, setConfig] = useState(null);
  const [targets, setTargets] = useState({});
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [frequency, setFrequency] = useState('weekly');
  const [maxPerSector, setMaxPerSector] = useState(5);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState('');
  const [expandedSectors, setExpandedSectors] = useState(new Set());
  const [showConfig, setShowConfig] = useState(false);
  const [error, setError] = useState('');

  // Region + vectors lens. Iter30b: region is per-sector via
  // `sectorRegions[sector] = { region, region_custom }`. The legacy
  // global `region` / `regionCustom` from iter30 is preserved as a
  // backwards-compatible default for round-trip but is no longer
  // surfaced in the UI as a global-region control.
  const [region, setRegion] = useState('');
  const [regionCustom, setRegionCustom] = useState('');
  const [regions, setRegions] = useState(['Global']);
  const [sectorRegions, setSectorRegions] = useState({});
  const [sectorVectors, setSectorVectors] = useState({});
  const [vectorsBySector, setVectorsBySector] = useState({});
  // Per-sector vector edit panel toggle (inline, inside the sector
  // accordion). Map from sector → bool.
  const [vectorEditOpen, setVectorEditOpen] = useState({});
  // Iter 32k — Result curation. `showDismissed` flips the GET
  // `/api/tracker/targets?include_dismissed=true` flag so muted
  // dismissed rows render alongside live ones with a Restore
  // action. `pendingConfirm` drives an inline confirmation modal
  // for bulk clear actions (sector / all). All curation calls are
  // tenant-scoped server-side; the UI is purely a renderer.
  const [showDismissed, setShowDismissed] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(null);  // { kind, sector?, message }

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    ...(apiKey ? { 'X-API-Key': apiKey } : {}),
  }), [apiKey]);

  // Load sectors and config on mount, OR re-load targets when the
  // "Show dismissed" toggle flips. The sectors / config endpoints
  // don't depend on `showDismissed`, so we only refetch them on
  // mount; the targets list reflects the current toggle state.
  useEffect(() => {
    const load = async () => {
      try {
        const targetsUrl = `${API_URL}/api/tracker/targets${
          showDismissed ? '?include_dismissed=true' : ''
        }`;
        const [sectorsResp, configResp, targetsResp] = await Promise.all([
          fetch(`${API_URL}/api/tracker/sectors`, { headers: headers() }),
          fetch(`${API_URL}/api/tracker/config`, { headers: headers() }),
          fetch(targetsUrl, { headers: headers() }),
        ]);
        const sectorsData = await sectorsResp.json();
        const configData = await configResp.json();
        const targetsData = await targetsResp.json();

        setSectors(sectorsData.sectors || []);
        setSectorLabels(sectorsData.labels || {});
        setProfiledSectors(sectorsData.profiled_sectors || []);
        setRegions(sectorsData.regions || ['Global']);
        setVectorsBySector(sectorsData.vectors || {});
        setConfig(configData);
        setSelectedSectors(configData.sectors || []);
        setFrequency(configData.frequency || 'weekly');
        setMaxPerSector(configData.max_per_sector || 5);
        setRegion(configData.region || '');
        setRegionCustom(configData.region_custom || '');
        setSectorRegions(configData.sector_regions || {});
        setSectorVectors(configData.vectors || {});
        setTargets(targetsData);

        // Default view is collapsed — user expands per sector on demand.
        setExpandedSectors(new Set());
      } catch (e) {
        setError('Failed to load tracker data');
      }
    };
    load();
  }, [headers, showDismissed]);

  const saveConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_URL}/api/tracker/config`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          sectors: selectedSectors,
          frequency, max_per_sector: maxPerSector, enabled: true,
          // Iter30b: legacy global region kept as default; per-sector
          // region overrides ride in sector_regions.
          region: region || '',
          region_custom: regionCustom || '',
          sector_regions: sectorRegions || {},
          vectors: sectorVectors || {},
        }),
      });
      if (!resp.ok) {
        // Iter 36d-fix-2 — surface useful backend validation detail
        // instead of the previous "Failed to save config" generic
        // toast. FastAPI returns 422 with a Pydantic error list at
        // `detail`; pick the first error and produce a human-readable
        // message. Falls back to the raw text on a non-JSON response.
        let friendly = '';
        try {
          const body = await resp.json();
          const detail = body && body.detail;
          if (Array.isArray(detail) && detail.length > 0) {
            const first = detail[0] || {};
            const field = Array.isArray(first.loc) && first.loc.length > 1
              ? first.loc.slice(1).join('.')
              : (first.loc || ['field']).join('.');
            const msg = first.msg || 'invalid value';
            if (field === 'max_per_sector') {
              friendly = 'Max Per Sector must be between 1 and 50';
            } else {
              friendly = `${field}: ${msg.replace(/^Value error, /, '')}`;
            }
          } else if (typeof detail === 'string') {
            friendly = detail;
          }
        } catch (_) {
          // Non-JSON body — leave `friendly` empty.
        }
        throw new Error(
          friendly
            ? `Failed to save config: ${friendly}`
            : `Failed to save config (HTTP ${resp.status})`
        );
      }
      const data = await resp.json();
      setConfig(data);
      setShowConfig(false);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // Per-sector inline lens editors. These run as the user toggles
  // controls inside each sector accordion section; the persisted save
  // happens lazily via `persistLens()` below so we don't blast the
  // backend on every checkbox click. Persist is debounced naturally by
  // the user's editing flow — clicking out of the panel triggers the
  // POST.
  const setSectorRegionPair = (sector, region_, region_custom_) => {
    setSectorRegions((prev) => ({
      ...prev,
      [sector]: {
        region: region_ ?? '',
        region_custom: region_custom_ ?? '',
      },
    }));
  };

  // Toggle a sector-scoped vector selection. Vectors only apply to
  // profiled sectors; the UI hides the vector group otherwise.
  const toggleVector = (sector, vector) => {
    setSectorVectors((prev) => {
      const current = new Set(prev[sector] || []);
      if (current.has(vector)) current.delete(vector);
      else current.add(vector);
      return { ...prev, [sector]: Array.from(current) };
    });
  };

  // Persist the current lens state for a single sector. Called on
  // explicit user actions (e.g. closing the inline editor) to avoid
  // chattering the backend.
  const persistLens = async () => {
    try {
      await fetch(`${API_URL}/api/tracker/config`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          sectors: config?.sectors || selectedSectors,
          frequency: config?.frequency || frequency,
          max_per_sector: config?.max_per_sector || maxPerSector,
          evidence_threshold: config?.evidence_threshold || 0,
          opportunity_threshold: config?.opportunity_threshold || 0,
          risk_threshold: config?.risk_threshold || 0,
          enabled: config?.enabled !== false,
          region: region || '',
          region_custom: regionCustom || '',
          sector_regions: sectorRegions || {},
          vectors: sectorVectors || {},
        }),
      });
    } catch { /* non-fatal — UI keeps the unsaved state */ }
  };

  // Iter 32k — Result curation helpers ──────────────────────────────
  // Soft-delete operations against the new tracker curation endpoints.
  // After every action we re-pull `/api/tracker/targets` (honouring
  // the `showDismissed` toggle) so the UI stays in sync without an
  // optimistic-update reconcile dance.
  const refetchTargets = async () => {
    try {
      const url = `${API_URL}/api/tracker/targets${
        showDismissed ? '?include_dismissed=true' : ''
      }`;
      const r = await fetch(url, { headers: headers() });
      if (r.ok) setTargets(await r.json());
    } catch { /* non-fatal */ }
  };

  const dismissTarget = async (targetId) => {
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/tracker/targets/${targetId}/dismiss`, {
        method: 'POST', headers: headers(),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(formatBackendDetail(d.detail) || 'Dismiss failed'); }
      await refetchTargets();
    } catch (e) { setError(trackerErrMsg(e)); }
  };

  const restoreTarget = async (targetId) => {
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/tracker/targets/${targetId}/restore`, {
        method: 'POST', headers: headers(),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(formatBackendDetail(d.detail) || 'Restore failed'); }
      await refetchTargets();
    } catch (e) { setError(trackerErrMsg(e)); }
  };

  const clearSectorTargets = async (sector) => {
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/tracker/targets/clear-sector`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ sector }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(formatBackendDetail(d.detail) || 'Clear failed'); }
      await refetchTargets();
    } catch (e) { setError(trackerErrMsg(e)); }
  };

  const clearAllTargets = async () => {
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/tracker/targets/clear-all`, {
        method: 'POST', headers: headers(),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(formatBackendDetail(d.detail) || 'Clear all failed'); }
      await refetchTargets();
    } catch (e) { setError(trackerErrMsg(e)); }
  };

  // Iter 32r — Hard-delete curation. Separate verbs from the soft
  // dismiss/clear family. Confirmation text is enforced by the
  // dialog copy below; these helpers just call the API.
  const deleteTarget = async (targetId) => {
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/tracker/targets/${targetId}`, {
        method: 'DELETE', headers: headers(),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(formatBackendDetail(d.detail) || 'Delete failed'); }
      await refetchTargets();
    } catch (e) { setError(trackerErrMsg(e)); }
  };

  const hardClearSector = async (sector) => {
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/tracker/targets/hard-clear-sector`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ sector }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(formatBackendDetail(d.detail) || 'Hard clear failed'); }
      await refetchTargets();
    } catch (e) { setError(trackerErrMsg(e)); }
  };

  const runTracker = async (sector) => {
    setScanning(true);
    setScanStatus(sector ? `Scanning ${sector}...` : 'Scanning all sectors...');
    setError('');
    try {
      const url = sector
        ? `${API_URL}/api/tracker/run?sector=${encodeURIComponent(sector)}`
        : `${API_URL}/api/tracker/run`;
      const resp = await fetch(url, { method: 'POST', headers: headers() });
      if (!resp.ok) { const d = await resp.json().catch(() => ({})); throw new Error(formatBackendDetail(d.detail) || 'Run failed'); }
      const data = await resp.json();
      const jobId = data.job_id;

      // Poll for completion
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const pollResp = await fetch(`${API_URL}/api/tracker/job/${jobId}`, { headers: headers() });
        if (!pollResp.ok) throw new Error(`Tracker job readback failed (${pollResp.status}); inspect existing job ${jobId} before starting another scan.`);
        const pollData = await pollResp.json();
        if (pollData.status === 'done') {
          const degraded = (pollData.sector_results || []).filter(result => result.degraded || result.search_status === 'failed' || result.debug?.provider_errors > 0);
          if (degraded.length) {
            setError(`Search failed or was incomplete for ${degraded.map(result => result.sector).join(', ')}. An empty result is not evidence that no opportunities exist. Existing saved targets remain available.`);
          }
          setScanStatus('Scan complete. Refreshing...');
          // Reload targets — preserve include_dismissed if user has
          // "Show dismissed" toggled on so the post-scan view stays
          // consistent with the toggle state.
          const reloadUrl = `${API_URL}/api/tracker/targets${
            showDismissed ? '?include_dismissed=true' : ''
          }`;
          const targetsResp = await fetch(reloadUrl, { headers: headers() });
          const targetsData = await targetsResp.json();
          setTargets(targetsData);
          // Reload config for last_run
          const configResp = await fetch(`${API_URL}/api/tracker/config`, { headers: headers() });
          setConfig(await configResp.json());
          setScanStatus('');
          // Expand scanned sectors
          if (sector) {
            setExpandedSectors(prev => new Set([...prev, sector]));
          } else {
            const expanded = new Set();
            for (const [s, tgts] of Object.entries(targetsData)) {
              if (tgts && tgts.length > 0) expanded.add(s);
            }
            setExpandedSectors(expanded);
          }
          break;
        } else if (pollData.status === 'failed') {
          throw new Error(pollData.error || 'Scan failed');
        }
        setScanStatus(`Scanning... (${Math.floor(i * 3)}s)`);
      }
    } catch (e) { setError(trackerErrMsg(e, 'scan')); }
    finally { setScanning(false); setScanStatus(''); }
  };

  const toggleSector = (sector) => {
    setExpandedSectors(prev => {
      const next = new Set(prev);
      if (next.has(sector)) next.delete(sector);
      else next.add(sector);
      return next;
    });
  };

  const toggleSectorSelection = (sector) => {
    setSelectedSectors(prev =>
      prev.includes(sector) ? prev.filter(s => s !== sector) : [...prev, sector]
    );
  };

  // Resolve a friendlier display label without mutating the canonical
  // sector value (which is what's persisted in tracker config and
  // target rows). Falls back to the canonical value verbatim when no
  // override exists.
  const labelFor = (sector) => sectorLabels[sector] || sector;
  const isProfiled = (sector) => profiledSectors.includes(sector);

  const totalTargets = Object.values(targets).reduce((sum, t) => sum + (t?.length || 0), 0);
  const newTargets = Object.values(targets).flat().filter(t => t?.trend === 'new').length;
  const risingTargets = Object.values(targets).flat().filter(t => t?.trend === 'rising').length;

  return (
    <div>
      {/* Header bar */}
      <div className="panel" style={{ marginBottom: '12px' }}>
        <div className="panel-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700' }} data-testid="tracker-title">
              Sector Tracker
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {config?.sectors?.length || 0} sector(s) tracked &middot;
              {' '}{totalTargets} target(s) surfaced
              {newTargets > 0 && <span style={{ color: 'var(--success)', fontWeight: '600' }}> &middot; {newTargets} new</span>}
              {risingTargets > 0 && <span style={{ color: 'var(--warning)', fontWeight: '600' }}> &middot; {risingTargets} rising</span>}
              {config?.last_run && (
                <span> &middot; Last run: {new Date(config.last_run).toLocaleString()}</span>
              )}
            </div>
            {/* Iter30b: per-sector lens summary replaces the prior
                single global Region/Vectors strip. We render one
                line per tracked sector that has a non-Global region
                or any selected vectors, so operators see at a
                glance which lens applies to which sector. */}
            {(config?.sectors || []).filter((s) => {
              const sr = sectorRegions[s] || {};
              const r = sr.region || region || '';
              const isGlobal = !r || r === 'Global';
              const hasVectors = (sectorVectors[s] || []).length > 0;
              return !isGlobal || hasVectors;
            }).length === 0 && (
              <div
                style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: 2 }}
                data-testid="tracker-lens-summary"
              >
                Region: <strong style={{ color: 'var(--text-primary)' }}>Global</strong>
              </div>
            )}
            <div style={{ marginTop: 2 }}>
              {(config?.sectors || []).map((s) => {
                const sr = sectorRegions[s] || {};
                const r = sr.region || region || '';
                const rc = sr.region_custom || regionCustom || '';
                const regLabel = (!r || r === 'Global')
                  ? 'Global'
                  : (r === 'Custom' ? (rc || 'Custom') : r);
                const vs = sectorVectors[s] || [];
                if (regLabel === 'Global' && vs.length === 0) return null;
                const slug = s.replace(/\s+/g, '-').toLowerCase();
                return (
                  <div
                    key={s}
                    data-testid={`tracker-sector-lens-summary-${slug}`}
                    style={{ fontSize: '11px', color: 'var(--text-secondary)' }}
                  >
                    <strong style={{ color: 'var(--text-primary)' }}>{labelFor(s)}</strong>
                    {' '}&middot; Region:{' '}
                    <span style={{ color: 'var(--text-primary)' }}>{regLabel}</span>
                    {vs.length > 0 && (
                      <span> &middot; Vectors: <span style={{ color: 'var(--text-primary)' }}>{vs.join(', ')}</span></span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Iter 32k — Show dismissed toggle. When on, dismissed
                rows render muted with a Restore action so demo prep
                is non-destructive and reversible. */}
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 4,
                       fontSize: 11, color: 'var(--text-secondary)',
                       cursor: 'pointer', userSelect: 'none' }}
              data-testid="tracker-show-dismissed-label"
            >
              <input
                type="checkbox"
                checked={showDismissed}
                onChange={(e) => setShowDismissed(e.target.checked)}
                data-testid="tracker-show-dismissed-toggle"
                style={{ margin: 0 }}
              />
              Show dismissed
            </label>
            <button className="btn btn-primary btn-sm" onClick={() => runTracker(null)}
              disabled={scanning || !config?.sectors?.length} data-testid="tracker-run-all-button">
              {scanning ? scanStatus || 'Scanning...' : 'Scan Now'}
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => setShowConfig(!showConfig)}
              data-testid="tracker-config-toggle">
              {showConfig ? 'Close Config' : 'Configure'}
            </button>
            {/* Iter 32k — Clear all is the highest-risk action so it's
                last in the row, outline-styled, and only enabled when
                there's at least one visible target across any sector.
                Backend rejects the call when Platform Root has no
                active tenant (400 active_tenant_required). */}
            {totalTargets > 0 && (
              <button
                className="btn btn-outline btn-sm"
                style={{ fontSize: 11, color: 'var(--text-secondary)' }}
                onClick={() => setPendingConfirm({
                  kind:    'clear_all',
                  message: `Clear all ${totalTargets} visible Tracker target(s) for the active tenant across every sector? This is a soft-delete — targets can be restored from "Show dismissed". Tracker config, vectors, schedules and scan history are preserved.`,
                })}
                data-testid="tracker-clear-all-button"
                title="Clear all visible Tracker results for the active tenant (soft-delete, reversible)"
              >
                Clear all results
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Iter 32k — bulk-clear confirmation modal */}
      {pendingConfirm && (
        <div
          data-testid="tracker-confirm-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setPendingConfirm(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="panel"
            style={{
              maxWidth: 480, width: '92%',
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
            }}
          >
            <div className="panel-header" style={{ fontWeight: 600 }}>
              {pendingConfirm.kind === 'clear_all' && 'Clear all Tracker results'}
              {pendingConfirm.kind === 'clear_sector' && 'Clear sector results'}
              {pendingConfirm.kind === 'delete_target' && 'Delete this tracker result?'}
              {pendingConfirm.kind === 'hard_clear_sector' && `Delete all ${pendingConfirm.sector || 'sector'} results?`}
            </div>
            <div className="panel-body" style={{ padding: 14, fontSize: 13, lineHeight: 1.5, color: 'var(--text-primary)' }}>
              {pendingConfirm.kind === 'delete_target'
                ? `Delete "${pendingConfirm.companyName || 'this result'}"? It may reappear if a future scan finds it again.`
                : pendingConfirm.kind === 'hard_clear_sector'
                  ? `Clear all ${pendingConfirm.sector} results? This removes current tracker results but keeps configuration, vectors and schedules. They can reappear if rediscovered by a future scan.`
                  : pendingConfirm.message}
            </div>
            <div className="panel-body" style={{
              padding: '10px 14px', display: 'flex', gap: 8,
              justifyContent: 'flex-end', borderTop: '1px solid var(--border)',
            }}>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setPendingConfirm(null)}
                data-testid="tracker-confirm-cancel"
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                style={
                  (pendingConfirm.kind === 'delete_target' || pendingConfirm.kind === 'hard_clear_sector')
                    ? { background: 'var(--danger)', borderColor: 'var(--danger)' }
                    : {}
                }
                onClick={async () => {
                  const pc = pendingConfirm;
                  setPendingConfirm(null);
                  if (pc.kind === 'clear_sector') await clearSectorTargets(pc.sector);
                  else if (pc.kind === 'clear_all') await clearAllTargets();
                  else if (pc.kind === 'delete_target') await deleteTarget(pc.targetId);
                  else if (pc.kind === 'hard_clear_sector') await hardClearSector(pc.sector);
                }}
                data-testid="tracker-confirm-apply"
              >
                {pendingConfirm.kind === 'clear_all' && 'Clear all'}
                {pendingConfirm.kind === 'clear_sector' && 'Clear sector'}
                {pendingConfirm.kind === 'delete_target' && 'Delete result'}
                {pendingConfirm.kind === 'hard_clear_sector' && 'Delete sector results'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="panel" style={{ marginBottom: '8px', borderLeft: '3px solid var(--danger)' }}>
          <div className="panel-body" style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--danger)' }}>{error}</div>
        </div>
      )}

      {/* Config panel */}
      {showConfig && (
        <div className="panel" style={{ marginBottom: '12px' }} data-testid="tracker-config-panel">
          <div className="panel-header">Tracker Configuration</div>
          <div className="panel-body" style={{ padding: '12px' }}>
            {/* Sector selection */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                Select Sectors
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {sectors.map(s => (
                  <button key={s} onClick={() => toggleSectorSelection(s)}
                    className={`btn btn-sm ${selectedSectors.includes(s) ? 'btn-primary' : 'btn-outline'}`}
                    style={{ fontSize: '11px', padding: '3px 8px' }}
                    title={isProfiled(s) ? `${s} — sector profile active` : s}
                    data-testid={`sector-toggle-${s.replace(/\s+/g, '-').toLowerCase()}`}>
                    {labelFor(s)}
                  </button>
                ))}
              </div>
            </div>

            {/* Frequency + max */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  Update Frequency
                </div>
                <GlassSelect className="signal-type-select" value={frequency} onChange={e => setFrequency(e.target.value)}
                  style={{ fontSize: '12px', padding: '5px 8px' }} data-testid="tracker-frequency-select">
                  {FREQUENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </GlassSelect>
              </div>
              <div>
                <div style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  Max per Sector
                </div>
                <input type="number" min="1" max="50" value={maxPerSector} onChange={e => setMaxPerSector(parseInt(e.target.value) || 5)}
                  style={{ width: '60px', padding: '5px 8px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--surface)' }}
                  data-testid="tracker-max-per-sector" />
              </div>
            </div>

            {/* Iter30b: Region + Vectors are configured PER SECTOR
                inside each sector's expanded accordion below — see
                `tracker-sector-lens-row-{slug}`. The configure panel
                only owns global frequency / max-per-sector.
                Earlier (iter30) iterations exposed a single global
                region selector here, which mistakenly implied a
                single region applied to every tracked sector.       */}

            <button className="btn btn-primary btn-sm" onClick={saveConfig} disabled={loading || selectedSectors.length === 0}
              data-testid="tracker-save-config">
              {loading ? 'Saving...' : `Save Config (${selectedSectors.length} sectors)`}
            </button>
          </div>
        </div>
      )}

      {/* Sector sections */}
      {(config?.sectors || []).map(sector => {
        const sectorTargets = targets[sector] || [];
        const sectorLensMap = (targets && targets._sector_lens) || {};
        const sectorLens = sectorLensMap[sector] || null;
        const isExpanded = expandedSectors.has(sector);
        const newCount = sectorTargets.filter(t => t.trend === 'new').length;
        const risingCount = sectorTargets.filter(t => t.trend === 'rising').length;

        return (
          <div key={sector} className="panel" style={{ marginBottom: '8px' }} data-testid={`tracker-sector-${sector.replace(/\s+/g, '-').toLowerCase()}`}>
            {/* Sector header — collapsible */}
            <div className="panel-body" style={{
              padding: '10px 16px', cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'space-between',
              borderBottom: isExpanded ? '1px solid var(--border)' : 'none',
            }} onClick={() => toggleSector(sector)}
              data-testid={`tracker-sector-toggle-${sector.replace(/\s+/g, '-').toLowerCase()}`}
              role="button"
              aria-expanded={isExpanded}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '10px', color: 'var(--text-secondary)', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                  &#9654;
                </span>
                <span style={{ fontSize: '14px', fontWeight: '600' }}>{labelFor(sector)}</span>
                <span className="badge badge-type" style={{ fontSize: '10px' }}>{sectorTargets.length} targets</span>
                {isProfiled(sector) && (
                  <span className="badge" style={{
                    fontSize: '9px',
                    background: 'rgba(186, 151, 123, 0.15)',
                    color: '#7c5a40',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontWeight: '600',
                    letterSpacing: '0.04em',
                  }} title="Sector intelligence profile active for this sector">
                    PROFILE
                  </span>
                )}
                {newCount > 0 && <span className="badge badge-high" style={{ fontSize: '10px' }}>{newCount} new</span>}
                {risingCount > 0 && <span className="badge badge-medium" style={{ fontSize: '10px' }}>{risingCount} rising</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button className="btn btn-outline btn-sm" style={{ fontSize: '10px', padding: '2px 8px' }}
                  onClick={(e) => { e.stopPropagation(); runTracker(sector); }}
                  disabled={scanning} data-testid={`scan-sector-${sector.replace(/\s+/g, '-').toLowerCase()}`}>
                  Scan
                </button>
                {/* Iter 32k — per-sector "Clear results" goes through the
                    confirmation modal so the operator can't accidentally
                    nuke a demo set with a stray click. Soft-delete only;
                    targets can be restored via "Show dismissed". */}
                {sectorTargets.filter(t => !t.dismissed).length > 0 && (
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ fontSize: '10px', padding: '2px 8px',
                             color: 'var(--text-secondary)' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingConfirm({
                        kind:    'clear_sector',
                        sector:  sector,
                        message: `Clear all ${sectorTargets.filter(t => !t.dismissed).length} visible target(s) for "${labelFor(sector)}"? This is a soft-delete — targets can be restored from "Show dismissed". Tracker config, vectors, schedules and scan history are preserved.`,
                      });
                    }}
                    data-testid={`clear-sector-${sector.replace(/\s+/g, '-').toLowerCase()}`}
                    title="Clear visible results for this sector (soft-delete, reversible)"
                  >
                    Clear results
                  </button>
                )}
                {/* Iter 32r — sector-level hard-delete. Removes every
                    row (live + dismissed) for this tenant + sector
                    only. Tracker config / vectors / schedules / region
                    are NOT touched. Confirmation enforced by the
                    pendingConfirm dialog. */}
                {sectorTargets.length > 0 && (
                  <button
                    className="btn btn-outline btn-sm"
                    style={{ fontSize: '10px', padding: '2px 8px',
                             color: 'var(--danger)',
                             borderColor: 'var(--danger)' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingConfirm({
                        kind:   'hard_clear_sector',
                        sector: sector,
                      });
                    }}
                    data-testid={`hard-clear-sector-${sector.replace(/\s+/g, '-').toLowerCase()}`}
                    title="Permanently delete all results for this sector (does not delete config; future scan may rediscover)"
                  >
                    Delete sector results
                  </button>
                )}
              </div>
            </div>

            {/* Expanded target list */}
            {isExpanded && (
              <>
                {/* Iter30b: per-sector Lens row — Region + Vectors
                    edit lives inside each sector accordion section so
                    different sectors can carry independent regions
                    and vector selections. The configure panel only
                    holds frequency / max-per-sector globals now. */}
                <div
                  data-testid={`tracker-sector-lens-row-${sector.replace(/\s+/g, '-').toLowerCase()}`}
                  style={{
                    padding: '10px 16px',
                    borderBottom: '1px solid var(--border)',
                    background: 'rgba(186,151,123,0.04)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    gap: 14,
                  }}
                >
                  {/* Region picker */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 220 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
                      Region (this sector)
                    </span>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <GlassSelect
                        className="signal-type-select"
                        value={(sectorRegions[sector] || {}).region || ''}
                        onChange={(e) => setSectorRegionPair(
                          sector, e.target.value,
                          (sectorRegions[sector] || {}).region_custom || '',
                        )}
                        onBlur={persistLens}
                        style={{ fontSize: '12px', padding: '5px 8px', minWidth: 190 }}
                        data-testid={`tracker-sector-region-select-${sector.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <option value="">Global / Any region</option>
                        {regions.filter((r) => r !== 'Global').map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </GlassSelect>
                      {((sectorRegions[sector] || {}).region === 'Custom') && (
                        <input
                          type="text"
                          placeholder="Custom region label"
                          value={(sectorRegions[sector] || {}).region_custom || ''}
                          onChange={(e) => setSectorRegionPair(
                            sector,
                            (sectorRegions[sector] || {}).region || '',
                            e.target.value,
                          )}
                          onBlur={persistLens}
                          data-testid={`tracker-sector-region-custom-${sector.replace(/\s+/g, '-').toLowerCase()}`}
                          style={{ fontSize: 12, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface)', minWidth: 200 }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Vectors edit (only for sectors with a vector
                      registry — the same gate the configure panel
                      used in iter30) */}
                  {(vectorsBySector[sector] || []).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 auto', minWidth: 320 }}>
                      <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
                        Vectors{(sectorVectors[sector] || []).length > 0 && ` (${(sectorVectors[sector] || []).length})`}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={() => setVectorEditOpen((prev) => {
                            const wasOpen = !!prev[sector];
                            const next = { ...prev, [sector]: !wasOpen };
                            // Persist when collapsing (avoids
                            // chattering on every checkbox click).
                            if (wasOpen) persistLens();
                            return next;
                          })}
                          style={{ fontSize: 11, padding: '3px 10px' }}
                          data-testid={`tracker-sector-vector-edit-${sector.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {vectorEditOpen[sector] ? 'Done' : 'Edit vectors'}
                        </button>
                        {(sectorVectors[sector] || []).length > 0 && !vectorEditOpen[sector] && (
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                            {(sectorVectors[sector] || []).slice(0, 3).join(', ')}
                            {(sectorVectors[sector] || []).length > 3 && ` +${(sectorVectors[sector] || []).length - 3}`}
                          </span>
                        )}
                        {(sectorVectors[sector] || []).length === 0 && !vectorEditOpen[sector] && (
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                            None &middot; broad sector profile
                          </span>
                        )}
                      </div>
                      {vectorEditOpen[sector] && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                          {(vectorsBySector[sector] || []).map((v) => {
                            const vSlug = v.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
                            const slug = sector.replace(/\s+/g, '-').toLowerCase();
                            const on = new Set(sectorVectors[sector] || []).has(v);
                            return (
                              <button
                                key={v}
                                type="button"
                                onClick={() => toggleVector(sector, v)}
                                className={`btn btn-sm ${on ? 'btn-primary' : 'btn-outline'}`}
                                style={{ fontSize: 10, padding: '3px 8px' }}
                                data-testid={`tracker-sector-vector-checkbox-${slug}-${vSlug}`}
                              >
                                {v}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {sectorLens && (
                  <div style={{ padding: '8px 16px 0' }}>
                    <SectorLensPanel
                      sectorLens={sectorLens}
                      selectedVectors={sectorVectors[sector] || []}
                      testIdPrefix={`tracker-lens-${sector.replace(/\s+/g, '-').toLowerCase()}`}
                    />
                  </div>
                )}
              </>
            )}

            {isExpanded && sectorTargets.length > 0 && (
              <div style={{ padding: '0' }}>
                <WideTableScroll testId={`tracker-table-scroll-${(sector || '').replace(/\s+/g, '-').toLowerCase()}`}>
                <table className="data-table" style={{ marginBottom: 0, tableLayout: 'fixed', width: '100%', minWidth: '1400px' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '80px' }}>Trend</th>
                      <th style={{ width: '20%' }}>Company</th>
                      <th style={{ width: '22%' }}>Reason Surfaced</th>
                      <th style={{ width: '90px' }} title="Marketplace role — Buyer / Supplier / Partner / Ecosystem / Verify / Platform. Visible only when an active marketplace-app vector classifies the row.">Role</th>
                      <th style={{ width: '70px' }} title="Number of distinct source results referencing this company">Signals</th>
                      <th style={{ width: '80px' }} title="Composite confidence from relevance, source diversity, and reason diversity">Confidence</th>
                      <th style={{ width: '80px' }} title="Delivery / transformation / operational risk. Uses Analyse results when available, otherwise a cautious tracker-level band.">Risk</th>
                      <th style={{ width: '90px' }} title="Priority — composite ranking. For marketplace-app vectors, includes role-aware uplift for schools, PTAs, youth clubs, and supply-side / ecosystem participants. Platform / lottery brands are dampened.">Priority</th>
                      <th style={{ width: '105px' }}>Last Updated</th>
                      <th style={{ width: '90px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectorTargets.map((t, i) => {
                      const trendStyle = TREND_STYLES[t.trend] || TREND_STYLES.stable;
                      return (
                        <React.Fragment key={i}>
                        <tr
                          data-testid={`tracker-target-${i}`}
                          style={t.dismissed ? { opacity: 0.55, fontStyle: 'italic' } : undefined}
                        >
                          <td>
                            <span className={`badge ${trendStyle.cls}`} style={{ fontSize: '10px' }}>
                              {trendStyle.label}
                            </span>
                          </td>
                          <td style={{ fontWeight: '600' }}>
                            {t.company_name}
                            {/* Iter 33c — Contact path / Evidence source
                                inline link. Renders nothing when neither
                                target_url nor best_evidence_url exists. */}
                            <ContactPathLink
                              targetUrl={t.target_url}
                              bestEvidenceUrl={t.best_evidence_url}
                              reason={t.target_url_reason}
                              testIdPrefix={`tracker-contact-path-${i}`}
                            />
                            {/* Iter 36f-1.5 — compact marketplace-role
                                sub-line. Rendered only when the row
                                carries `recommended_role` AND a non-empty
                                `marketplace_role_explanation`. Plain text,
                                no badge — the role badge lives in its own
                                column. */}
                            {(() => {
                              const sub = getRoleSubline(t);
                              if (!sub) return null;
                              return (
                                <div
                                  data-testid={`tracker-role-subline-${i}`}
                                  style={{
                                    fontWeight: 400,
                                    fontSize: '11px',
                                    color: 'var(--text-secondary)',
                                    marginTop: '2px',
                                    lineHeight: 1.35,
                                  }}
                                  title={sub}
                                >
                                  {sub}
                                </div>
                              );
                            })()}
                            {t.dissolved_state?.is_dissolved && (() => {
                              // Iter 37 / 37-fix — chip label branches
                              // on entity_viability (Primus-class
                              // blocker vs Coinbase-class warning).
                              const ds = t.dissolved_state || {};
                              const v = ds.entity_viability || '';
                              let label;
                              if (v === 'dissolved_uk_entity_blocker') {
                                label = 'UK legal entity dissolved';
                              } else if (v === 'global_brand_uk_entity_warning') {
                                label = 'UK entity warning';
                              } else if (v === 'match_uncertain_verify') {
                                label = 'UK entity match uncertain';
                              } else {
                                // Legacy fallback for pre-37-fix payloads.
                                const mc = (ds.name_match_confidence || '').toLowerCase();
                                const isGlobalBrand = !!ds.global_brand_match;
                                label = (mc === 'near' || mc === 'loose' || isGlobalBrand)
                                  ? 'UK entity match uncertain'
                                  : `UK entity ${ds.status_label || 'inactive'}`;
                              }
                              const tooltip =
                                'Companies House status applies to the matched UK ' +
                                'legal entity only. For UK-only entities, a dissolved ' +
                                'status may mean this legal entity should not be ' +
                                'pursued unless a successor or related trading entity ' +
                                'exists. For international brands, verify the current ' +
                                'UK operating entity.';
                              return (
                                <span
                                  data-testid={`tracker-dissolved-chip-${i}`}
                                  title={tooltip}
                                  style={{
                                    marginLeft: '6px',
                                    fontSize: '9px',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    padding: '2px 6px',
                                    borderRadius: '999px',
                                    background: 'rgba(120, 113, 108, 0.16)',
                                    color: 'var(--text-secondary)',
                                    border: '1px solid rgba(120, 113, 108, 0.3)',
                                    verticalAlign: 'middle',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {label}
                                </span>
                              );
                            })()}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                              {(t.reasons_surfaced || []).map((r, j) => (
                                <span key={j} className="badge badge-type" style={{ fontSize: '9px' }}>{r}</span>
                              ))}
                            </div>
                          </td>
                          {/* Iter 36f-1.5 — Role chip column. Empty cell
                              for non-marketplace rows. Chip label and
                              colour come from the pure helper so the
                              contract is testable in isolation. */}
                          <td>
                            {(() => {
                              const chip = getRoleChip(t);
                              if (!chip) {
                                return (
                                  <span
                                    data-testid={`tracker-role-${i}`}
                                    style={{ color: 'var(--text-secondary)', fontSize: '12px' }}
                                  >
                                    —
                                  </span>
                                );
                              }
                              return (
                                <span
                                  className={chip.className}
                                  style={{ fontSize: '10px' }}
                                  title={chip.tooltip}
                                  data-testid={`tracker-role-${i}`}
                                  data-role={chip.role}
                                >
                                  {chip.label}
                                </span>
                              );
                            })()}
                          </td>
                          <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px' }}>{t.signal_count}</td>
                          <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px' }}>
                            {Math.round((t.avg_confidence || 0) * 100)}%
                          </td>
                          {/* Risk — numeric from Analyse when present, else heuristic band. */}
                          <td>
                            {(() => {
                              const numeric = (typeof t.risk_score === 'number') ? t.risk_score : null;
                              const band = t.risk_band || null;
                              if (numeric === null && !band) {
                                return <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>—</span>;
                              }
                              const label = numeric !== null ? String(numeric) : band;
                              const cls = numeric !== null
                                ? (numeric >= 70 ? 'badge badge-high' : numeric >= 40 ? 'badge badge-medium' : 'badge badge-low')
                                : (band === 'High' ? 'badge badge-high' : band === 'Medium' ? 'badge badge-medium' : 'badge badge-low');
                              return (
                                <span
                                  className={cls}
                                  style={{ fontSize: '10px' }}
                                  title={t.score_source === 'analysis' ? 'From latest Analyse result' : 'Heuristic — no Analyse result yet'}
                                  data-testid={`tracker-risk-${i}`}
                                >
                                  {label}
                                </span>
                              );
                            })()}
                          </td>
                          {/* Iter 36f-1.5 — Priority cell.
                              Renders the post-Iter-36f-1
                              `tracker_priority_score` that drives row
                              ordering, so the visible value and the row
                              position tell the same story. The legacy
                              `opportunity_score` / `opportunity_band`
                              values are surfaced in the tooltip for
                              power users. Existing `tracker-opp-${i}`
                              testid is preserved for backward compat
                              with current Playwright suites; new
                              `tracker-priority-${i}` testid added for
                              the role-aware UI tests. */}
                          <td>
                            {(() => {
                              const score = getPriorityValue(t);
                              if (score === null) {
                                return (
                                  <span
                                    data-testid={`tracker-opp-${i}`}
                                    style={{ color: 'var(--text-secondary)', fontSize: '12px' }}
                                  >
                                    —
                                  </span>
                                );
                              }
                              const cls = getPriorityColorClass(score);
                              const tooltip = getPriorityTooltip(t);
                              return (
                                <span
                                  className={cls}
                                  style={{ fontSize: '10px' }}
                                  title={tooltip}
                                  data-testid={`tracker-priority-${i}`}
                                  data-priority={score}
                                >
                                  <span data-testid={`tracker-opp-${i}`} style={{ display: 'inline' }}>
                                    {score}
                                  </span>
                                </span>
                              );
                            })()}
                          </td>
                          <td style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            {t.last_updated ? new Date(t.last_updated).toLocaleDateString() : '-'}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <button className="btn btn-outline btn-sm" style={{ fontSize: '10px', padding: '2px 8px' }}
                                onClick={() => onSelectCompany && onSelectCompany({
                                  name: t.company_name,
                                  industry: sector,
                                  // Iter 33b — Target URL / Contact path
                                  // carry-through. Forward the discovered
                                  // URL so Scout's CompanyInput can
                                  // pre-populate the website field.
                                  target_url: t.target_url,
                                  target_url_source_type: t.target_url_source_type,
                                  target_url_confidence: t.target_url_confidence,
                                  target_url_reason: t.target_url_reason,
                                  best_evidence_url: t.best_evidence_url,
                                  best_evidence_title: t.best_evidence_title,
                                })} data-testid={`investigate-${i}`}>
                                Investigate
                              </button>
                              {/* Iter 32k — per-target curation. Dismissed
                                  rows show a Restore button instead so the
                                  operator can undo without a full reload. */}
                              {t.dismissed ? (
                                <button
                                  className="btn btn-outline btn-sm"
                                  style={{
                                    fontSize: '10px',
                                    padding: '2px 8px',
                                    color: 'var(--success)',
                                    borderColor: 'var(--success)',
                                  }}
                                  onClick={() => restoreTarget(t.id)}
                                  data-testid={`tracker-restore-${i}`}
                                  title="Restore this target to the live results"
                                >
                                  Restore
                                </button>
                              ) : (
                                <button
                                  className="btn btn-outline btn-sm"
                                  style={{
                                    fontSize: '10px',
                                    padding: '2px 8px',
                                    color: 'var(--text-secondary)',
                                  }}
                                  onClick={() => dismissTarget(t.id)}
                                  data-testid={`tracker-dismiss-${i}`}
                                  title="Hide this target from the visible results (soft-delete; can be restored)"
                                >
                                  Dismiss
                                </button>
                              )}
                              {/* Iter 32r — per-target hard-delete. Separate
                                  verb from Dismiss/Restore; confirmation
                                  enforced via pendingConfirm dialog so a
                                  misclick cannot remove a row. */}
                              <button
                                className="btn btn-outline btn-sm"
                                style={{
                                  fontSize: '10px',
                                  padding: '2px 8px',
                                  color: 'var(--danger)',
                                  borderColor: 'var(--danger)',
                                }}
                                onClick={() => setPendingConfirm({
                                  kind: 'delete_target',
                                  targetId: t.id,
                                  companyName: t.company_name,
                                  sector,
                                })}
                                data-testid={`tracker-delete-${i}`}
                                title="Permanently delete this tracker result. A future scan may recreate it if rediscovered."
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                        {/* Vector suggestions for complex companies */}
                        {t.is_complex && t.suggested_vectors && t.suggested_vectors.length > 0 && (
                          <tr>
                            <td colSpan={9} style={{ padding: '4px 16px 8px 48px', background: 'var(--bg)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }} data-testid={`vectors-${i}`}>
                                <span style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  Suggested vectors:
                                </span>
                                {t.suggested_vectors.map((v, vi) => (
                                  <span key={vi} className="badge badge-type" style={{ fontSize: '10px' }}
                                    title={v.sub_vectors ? `Sub-vectors: ${v.sub_vectors.join(', ')}` : ''}>
                                    {v.vector}
                                  </span>
                                ))}
                                <button className="btn btn-outline btn-sm" style={{ fontSize: '9px', padding: '1px 6px', marginLeft: '4px' }}
                                  onClick={() => onSelectCompany && onSelectCompany({
                                    name: t.company_name,
                                    industry: sector,
                                    vector: t.suggested_vectors[0]?.vector,
                                  })} data-testid={`investigate-vector-${i}`}>
                                  Investigate by vector
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                </WideTableScroll>
              </div>
            )}

            {isExpanded && sectorTargets.length === 0 && (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px' }}>
                No targets surfaced yet. Click <strong>Scan</strong> to discover companies in this sector.
              </div>
            )}
          </div>
        );
      })}

      {/* Empty state */}
      {(!config?.sectors || config.sectors.length === 0) && !showConfig && (
        <div className="panel">
          <div className="panel-body" style={{ padding: '32px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>No sectors configured</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
              Select sectors to begin tracking emerging companies and opportunities.
            </div>
            <button className="btn btn-primary" onClick={() => setShowConfig(true)} data-testid="tracker-setup-button">
              Configure Tracker
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Iter 39 / Slice 1 — Tracker mode tabs ─────────────────────────────────
//
// `TrackerDashboard` is now a thin wrapper that toggles between the
// existing Industry Sector dashboard (verbatim, unchanged) and the
// new Technology Sector dashboard. Mode lives in the URL query
// (`?mode=industry|technology`, default `industry`) so deep links and
// the browser back button work cleanly inside the tenant route
// `/t/<slug>/tracker`.
import TrackerTechnologyDashboard from './TrackerTechnologyDashboard';

export default function TrackerDashboard(props) {
  const url = new URL(window.location.href);
  const initialMode = url.searchParams.get('mode') === 'technology' ? 'technology' : 'industry';
  const [mode, setMode] = useState(initialMode);

  const switchMode = (next) => {
    setMode(next);
    const u = new URL(window.location.href);
    if (next === 'industry') u.searchParams.delete('mode');
    else u.searchParams.set('mode', next);
    window.history.replaceState({}, '', u.toString());
  };

  return (
    <div data-testid="tracker-dashboard-root">
      <div
        role="tablist"
        aria-label="Tracker mode"
        style={{
          display: 'flex', gap: 8, padding: '12px 16px 0',
          borderBottom: '1px solid var(--border-color, #1f2630)',
        }}
        data-testid="tracker-mode-tabs"
      >
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'industry'}
          onClick={() => switchMode('industry')}
          data-testid="tracker-mode-industry-tab"
          style={{
            background: 'transparent', border: 'none', padding: '10px 14px',
            color: mode === 'industry' ? 'var(--accent, #4ea1ff)' : 'var(--text-secondary, #8b95a3)',
            borderBottom: mode === 'industry' ? '2px solid var(--accent, #4ea1ff)' : '2px solid transparent',
            cursor: 'pointer', fontWeight: 600, fontSize: 14,
          }}
        >
          Industry Sector
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'technology'}
          onClick={() => switchMode('technology')}
          data-testid="tracker-mode-technology-tab"
          style={{
            background: 'transparent', border: 'none', padding: '10px 14px',
            color: mode === 'technology' ? 'var(--accent, #4ea1ff)' : 'var(--text-secondary, #8b95a3)',
            borderBottom: mode === 'technology' ? '2px solid var(--accent, #4ea1ff)' : '2px solid transparent',
            cursor: 'pointer', fontWeight: 600, fontSize: 14,
          }}
        >
          Technology Sector
        </button>
      </div>

      {mode === 'industry'
        ? <IndustryTrackerDashboard {...props} />
        : <TrackerTechnologyDashboard {...props} />}
    </div>
  );
}
